import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@workspace/ui/lib/utils";
import { Icon } from "../atoms/icon.js";
import { Button } from "../atoms/button.js";

const pdfPreviewVariants = cva(
    "w-full h-full border-0",
    {
        variants: {
            size: {
                sm: "min-h-[400px]",
                md: "min-h-[600px]",
                lg: "min-h-[800px]",
                full: "min-h-[90vh]",
            },
        },
        defaultVariants: {
            size: "lg",
        },
    }
);

export interface PDFPreviewModalProps
    extends VariantProps<typeof pdfPreviewVariants> {
    isOpen: boolean;
    onClose: () => void;
    pdfUrl: string | null;
    fileName: string;
    onDownload?: () => void;
    title?: string;
    className?: string;
}

export const PDFPreviewModal = React.forwardRef<HTMLDivElement, PDFPreviewModalProps>(
    ({
        isOpen,
        onClose,
        pdfUrl,
        fileName,
        onDownload,
        title = "PDF Preview",
        size = "lg",
        className,
        ...props
    }, ref) => {
        const [isLoading, setIsLoading] = React.useState(true);
        const [hasError, setHasError] = React.useState(false);

        React.useEffect(() => {
            if (isOpen && pdfUrl) {
                setIsLoading(true);
                setHasError(false);
            }
        }, [isOpen, pdfUrl]);

        const handleIframeLoad = () => {
            setIsLoading(false);
        };

        const handleIframeError = () => {
            setIsLoading(false);
            setHasError(true);
        };

        const handleDownload = async () => {
            if (!pdfUrl || !fileName) {
                console.error("❌ No PDF URL or fileName available for download");
                return;
            }

            if (onDownload) {
                onDownload();
                return;
            }

            try {
                const response = await fetch(pdfUrl);
                if (!response.ok) throw new Error('Failed to fetch PDF');

                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } catch (error) {
                console.error("❌ Download failed:", error);
            }
        };

        React.useEffect(() => {
            if (isOpen) {
                document.body.style.overflow = "hidden";
            } else {
                document.body.style.overflow = "";
            }
            return () => {
                document.body.style.overflow = "";
            };
        }, [isOpen]);

        if (!isOpen) return null;

        return (
            <div
                className="fixed inset-0 z-50 bg-black/80"
                onClick={onClose}
                aria-modal="true"
                role="dialog"
            >
                <div
                    ref={ref}
                    className={cn(
                        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-[95vw] max-h-[95vh] translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background shadow-lg duration-200 rounded-lg",
                        className
                    )}
                    onClick={(e) => e.stopPropagation()}
                    {...props}
                >
                    <div className="flex flex-col h-full">
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 border-b">
                            <h2 className="text-lg font-semibold">{title}</h2>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleDownload}
                                    disabled={!pdfUrl}
                                >
                                    <Icon name="download" size="sm" />
                                    Download
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={onClose}
                                >
                                    <Icon name="close" size="sm" />
                                </Button>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 relative">
                            {isLoading && (
                                <div className="absolute inset-0 flex items-center justify-center bg-background">
                                    <div className="flex items-center gap-2">
                                        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                                        <span>Loading PDF...</span>
                                    </div>
                                </div>
                            )}

                            {hasError && (
                                <div className="absolute inset-0 flex items-center justify-center bg-background">
                                    <div className="text-center">
                                        <Icon name="alert-circle" size="lg" className="mx-auto mb-2 text-destructive" />
                                        <p className="text-lg font-medium">Failed to load PDF</p>
                                        <p className="text-sm text-muted-foreground">
                                            The PDF could not be displayed. Try downloading it instead.
                                        </p>
                                        <Button
                                            variant="outline"
                                            className="mt-4"
                                            onClick={handleDownload}
                                            disabled={!pdfUrl}
                                        >
                                            <Icon name="download" size="sm" />
                                            Download PDF
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {pdfUrl && (
                                <iframe
                                    src={`${pdfUrl}#toolbar=1&navpanes=1&scrollbar=1`}
                                    className={cn(pdfPreviewVariants({ size }))}
                                    onLoad={handleIframeLoad}
                                    onError={handleIframeError}
                                    title="PDF Preview"
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
);

PDFPreviewModal.displayName = "PDFPreviewModal";

// Default export for backward compatibility
export default PDFPreviewModal;
